/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {isBottomSheetTableView} from '@ion/dls/src/components/sidesheet/helpers';
import {setMobileListVisible, setSideSheetStatus} from '@/store/actions/nav';
import {
  mobileListVisibleSelector,
  sideSheetSelector
} from '@/store/selectors/nav-selectors';
import {
  assetDetailViewSelector,
  selectedAssetSelector,
  sideSheetViewSelector
} from '@/store/selectors/tracking-selectors';

/**
 * Custom hook to handle the toggle logic for the side sheet.
 * @returns {object} An object containing the state and functions for toggling the side sheet.
 */
const useToggleSideSheet = () => {
  const dispatch = useDispatch();
  const sideSheetStatus        = useSelector(sideSheetSelector);
  const sideSheetView          = useSelector(sideSheetViewSelector);
  const assetDetailView        = useSelector(assetDetailViewSelector);
  const selectedAsset          = useSelector(selectedAssetSelector);
  const isMobileListVisible    = useSelector(mobileListVisibleSelector);
  const isDesktopSideSheetOpen = sideSheetStatus.desktopSideSheetOpen;

  /**
   * Toggles the side sheet based on the current state.
   * @param {object} options - Options for toggling the side sheet.
   * @param {string} options.page - The page identifier for the side sheet.
   * @param {boolean} [options.desktopStatus=false] - The desired desktop side sheet status.
   * @param {boolean} [options.mobileStatus=false] - The desired mobile side sheet status.
   */
  const toggleSideSheet = useCallback(
    ({page, desktopStatus = false, mobileStatus = false}) => {
      dispatch(
        setSideSheetStatus({
          activeSideSheet      : page,
          desktopSideSheetOpen : desktopStatus,
          mobileSideSheetOpen  : mobileStatus
        })
      );
    },
    [dispatch]
  );

  /**
   * Handles the click event for toggling the side sheet.
   */
  const onToggleClick = useCallback(() => {
    toggleSideSheet({
      page          : 'tracking',
      desktopStatus : !isDesktopSideSheetOpen
    });
  }, [toggleSideSheet, isDesktopSideSheetOpen]);

  /**
   * Unconditionally closes the side sheet on both desktop and mobile -
   * unlike `onToggleClick`, which flips the current desktop state (and so
   * could reopen it), this is for a dedicated Close control that must
   * always close regardless of the current state. Also hides the mobile
   * list's bottom sheet (`mobileListVisible`) - a separate flag from
   * `mobileSideSheetOpen`, which already means "asset detail view is open"
   * elsewhere and defaults closed, so reusing it here would hide the list
   * by default on every mobile page load.
   */
  const onCloseSideSheet = useCallback(() => {
    toggleSideSheet({page: 'tracking'});
    dispatch(setMobileListVisible(false));
  }, [toggleSideSheet, dispatch]);

  /**
   * Reopens the mobile list's bottom sheet after `onCloseSideSheet` hid it.
   */
  const onReopenMobileList = useCallback(() => {
    dispatch(setMobileListVisible(true));
  }, [dispatch]);

  /**
   * Determines whether the side sheet should be shown based on the current view and asset selection.
   */
  const showSideSheet = useMemo(() => (
    isBottomSheetTableView(sideSheetView) ? !!selectedAsset && assetDetailView : true
  ), [sideSheetView, selectedAsset]);

  return {
    sideSheetView,
    assetDetailView,
    isDesktopSideSheetOpen,
    isMobileListVisible,
    onToggleClick,
    onCloseSideSheet,
    onReopenMobileList,
    showSideSheet
  };
};

export default useToggleSideSheet;
